import React, { useState } from 'react'
import { usePopper } from 'react-popper'

import { Box } from '@driftt/dds'

import FadeInAnimation from 'components/FadeInAnimation'
import Portal from 'components/Portal'
import { useDebouncedCallback } from 'hooks/useDebouncedCallback'
import { segmentTrack, TrackType } from 'utils/tracking'

import styles from './styles.module.css'

const TooltipHoverPortal: React.FC<{
  children: React.ReactElement
  tooltipBody: React.ReactElement
  refElement: HTMLDivElement | null
  boundaryRef: HTMLDivElement | null
  placement?: any
  className?: string
  trackingEventName?: string
  isAlwaysOpen?: boolean
  offset?: [number, number]
}> = ({
  trackingEventName,
  placement = 'left-end',
  refElement,
  boundaryRef,
  tooltipBody,
  className,
  isAlwaysOpen = false,
  children,
  offset = [200, 15]
}) => {
  const [isShown, setIsShown] = useState(isAlwaysOpen)

  const debouncedTrackingEvent = useDebouncedCallback(() => {
    if (trackingEventName && trackingEventName in TrackType) {
      segmentTrack(trackingEventName as TrackType)
    }
  }, 700)

  const debouncedMouseEvent = useDebouncedCallback((toggle: boolean) => {
    if (!isAlwaysOpen) {
      setIsShown(toggle)
      toggle && debouncedTrackingEvent()
    }
  }, 100)

  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)
  const { styles: popperStyles, attributes } = usePopper(refElement, popperElement, {
    placement,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset
        }
      },
      {
        name: 'preventOverflow',
        options: {
          rootBoundary: 'document',
          boundary: boundaryRef ?? 'clippingParents',
          mainAxis: true,
          altAxis: false,
          padding: 0,
          altBoundary: true,
          tether: false
        }
      }
    ]
  })

  return (
    <div
      className={className}
      data-ocweb-id={trackingEventName}
      onMouseEnter={() => {
        debouncedMouseEvent(true)
      }}
      onMouseLeave={() => debouncedMouseEvent(false)}>
      {children}
      {isShown && (
        <FadeInAnimation delay={300} inProp={isShown}>
          <Portal>
            <Box
              className={styles.popper}
              ref={setPopperElement}
              style={popperStyles.popper}
              {...attributes.popper}>
              {tooltipBody}
            </Box>
          </Portal>
        </FadeInAnimation>
      )}
    </div>
  )
}

export default TooltipHoverPortal
